export interface SSEFrame {
  id?: string
  event?: string
  data: string
}

/**
 * Splits an SSE buffer into complete frames (each `\n\n`-terminated) and returns
 * the trailing partial for the next chunk. Captures `id:` (an opaque resume cursor)
 * and concatenates multiple `data:` lines per the SSE spec. Pure — unit tested.
 *
 * Shared by the POST stream util (utils/sse-stream) and the vibe-agent GET viewer
 * (the vibe-agent transport (flow/.../vibe-agent/transport)). Single-line `data:` frames — the only kind
 * our services emit — parse identically to a last-line-wins reader, so the shared
 * concatenation is behavior-neutral for existing consumers.
 */
export const parseSSEFrames = (buffer: string): { frames: SSEFrame[]; rest: string } => {
  // The SSE spec allows CRLF/CR line endings; without normalizing, a CRLF
  // stream never matches '\n\n' and the whole stream piles into `rest`.
  const parts = buffer.replace(/\r\n?/g, '\n').split('\n\n')
  const rest = parts.pop() ?? ''
  const frames: SSEFrame[] = []

  for (const part of parts) {
    let id: string | undefined
    let event: string | undefined
    let data = ''

    for (const line of part.split('\n')) {
      if (line.startsWith('id:')) {
        id = line.slice(3).trim()
      } else if (line.startsWith('event:')) {
        event = line.slice(6).trim()
      } else if (line.startsWith('data:')) {
        const value = line.slice(5).trim()
        data = data ? `${data}\n${value}` : value
      }
    }

    if (data || id) frames.push({ id, event, data })
  }

  return { frames, rest }
}
